/**
 * DeploySnapshotPage
 * ------------------
 * Lives behind the Settings password gate. Surfaces the DB snapshot
 * status, lets admin take a fresh snapshot before clicking Deploy,
 * and exposes a manual restore button as a fallback.
 *
 * Why it exists: The production environment provisions a fresh MongoDB for the deployed
 * app — preview data does NOT automatically carry over. The backend
 * dumps every (non-blob) collection into `/app/data-snapshots/latest`
 * which is then part of the deployed code bundle. When the production
 * backend boots and finds the new DB empty, it automatically imports
 * the bundled snapshot before serving the first request.
 */
import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { SettingsGate } from "@/components/SettingsGate";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Database, Download, Upload, RefreshCw, ShieldAlert, Clock, FileArchive } from "lucide-react";
import { toast } from "sonner";

function fmtSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtIST(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}

export default function DeploySnapshotPage() {
  return (
    <SettingsGate title="Deploy Snapshot" subtitle="Database carry-over to the deployed app">
      <DeploySnapshotInner />
    </SettingsGate>
  );
}

function DeploySnapshotInner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/db/snapshot");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load snapshot status");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const takeNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/db/snapshot");
      toast.success(
        `Snapshot taken — ${data.meta.total_documents.toLocaleString()} docs (${fmtSize(data.meta.total_bytes)})`,
      );
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to take snapshot");
    } finally {
      setBusy(false);
    }
  };

  const restoreNow = async () => {
    if (restoreBusy) return;
    if (!window.confirm(
      "Restore the database from the bundled snapshot? This will upsert every document from the snapshot into the current DB. Only use this on a fresh production database — running it on a populated DB is safe but can take a few minutes.",
    )) return;
    setRestoreBusy(true);
    try {
      const { data } = await api.post("/admin/db/restore");
      toast.success(
        `Restored ${data.meta.total_documents.toLocaleString()} docs in ${data.meta.duration_seconds}s`,
      );
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to restore snapshot");
    } finally {
      setRestoreBusy(false);
    }
  };

  const meta = status?.meta;
  const colls = meta?.collections || {};
  const docTotal = meta?.total_documents || 0;
  const collEntries = Object.entries(colls).sort(([, a], [, b]) => b - a);

  return (
    <AppShell title="Deploy Snapshot" subtitle="Database carry-over to the deployed app">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-primary" />
              Current snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {!status ? (
              <div className="text-muted-foreground">Loading…</div>
            ) : !status.snapshot_present ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <ShieldAlert className="mr-1.5 inline h-4 w-4" />
                No snapshot on disk yet. Click <span className="font-medium">Take snapshot now</span>{" "}
                below to capture the current DB before deploying.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Documents" value={docTotal.toLocaleString()} testid="snap-docs" />
                  <Stat label="Size" value={fmtSize(meta.total_bytes)} testid="snap-size" />
                  <Stat
                    label="Collections"
                    value={Object.keys(colls).length}
                    testid="snap-collections"
                  />
                  <Stat
                    label="Taken at (IST)"
                    value={fmtIST(meta.taken_at)}
                    testid="snap-taken-at"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <Badge variant="outline">
                    <Clock className="mr-1 h-3 w-3" /> Duration {meta.duration_seconds}s
                  </Badge>
                  <Badge
                    variant="outline"
                    className={meta.include_photos ? "border-emerald-300 bg-emerald-50 text-emerald-800" : ""}
                  >
                    {meta.include_photos ? "With photo blobs" : "Metadata only (no photos)"}
                  </Badge>
                  {status.restored_mark ? (
                    <Badge className="bg-violet-600 text-white hover:bg-violet-600">
                      Production · Already restored once
                    </Badge>
                  ) : null}
                </div>
                {collEntries.length ? (
                  <details className="rounded-md border bg-slate-50/60 p-2 text-xs">
                    <summary className="cursor-pointer font-medium text-slate-700">
                      Per-collection counts
                    </summary>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3">
                      {collEntries.map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between font-mono">
                          <span className="truncate text-slate-600">{k}</span>
                          <span className="font-semibold text-slate-900">{v.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileArchive className="h-4 w-4 text-primary" />
              Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Button onClick={takeNow} disabled={busy} data-testid="snap-take-btn">
              <Download className={`mr-2 h-4 w-4 ${busy ? "animate-pulse" : ""}`} />
              {busy ? "Capturing…" : "Take snapshot now"}
            </Button>
            <Button
              variant="outline"
              onClick={restoreNow}
              disabled={restoreBusy || !status?.snapshot_present}
              data-testid="snap-restore-btn"
            >
              <Upload className={`mr-2 h-4 w-4 ${restoreBusy ? "animate-pulse" : ""}`} />
              {restoreBusy ? "Restoring…" : "Restore from snapshot"}
            </Button>
            <Button variant="ghost" size="sm" onClick={load} data-testid="snap-refresh-btn">
              <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh status
            </Button>
            <div className="mt-2 rounded-md border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-900">
              <span className="font-semibold">How this works:</span> The snapshot lives at{" "}
              <code className="rounded bg-white/70 px-1">/app/data-snapshots/latest</code> inside the
              code bundle. When you click Deploy, the platform ships that directory along with the code
              to a fresh production environment. The production backend auto-restores from it on
              its first startup if the DB is empty.
              <br />
              <br />
              <strong>Full data including photos, selfies and ID-card attachments are bundled</strong>{" "}
              by default. Bundle size can grow to a few hundred MB — that's normal and deploys fine.
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md border bg-white p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate font-semibold text-slate-900">{value}</div>
    </div>
  );
}
